<script setup lang="ts">
import {Dialog, DialogScrollContent, DialogTitle, DialogTrigger} from "@/core/components/ui/dialog";
import {Sharing, SharingStatus as ISharingStatus} from "@/core/interfaces/Sharing";
import HospitalPatientFileRow from "@/hospital/components/hospital-patient-file-row/HospitalPatientFileRow.vue";
import dateHelper from "@/core/helpers/dateHelper";
import {sharingApi} from "@/container";
import {reactive, ref} from "vue";
import {Textarea} from "@/core/components/ui/textarea";
import {useLiveStore} from "@/stores/live.store";
import {LiveItemType} from "@/core/interfaces/live/LiveItem";
import BaseButton from "@/core/components/base/BaseButton.vue";
import SharingStatus from "@/hospital/components/sharing/SharingStatus.vue";
import {useToastStore} from "@/stores/toast.store";

const toast = useToastStore();
const props = defineProps<{
    sharing: Sharing
}>();

const emit = defineEmits<{
    sharingUpdated: [sharing: Sharing]
}>();

const liveStore = useLiveStore();

const loadings = reactive({
    accept: false,
    refuse: false
});

const comment = ref("");

/**
 * Remove the sharing item from the live store.
 */
const removeFromStore = () => {
    liveStore.deleteItems(props.sharing, LiveItemType.SHARING);
};

const acceptSharing = async () => {
    loadings.accept = true;
    try {
        const updatedSharing = await sharingApi().accept(props.sharing.id, comment.value);
        removeFromStore();
        emit('sharingUpdated', updatedSharing);
    } catch {
        toast.showError({
            content: "Une erreur est survenue lors de l'acceptation du partage. Veuillez réessayer plus tard.",
        });
    } finally {
        loadings.accept = false;
    }
};

const declineSharing = async () => {
    loadings.refuse = true;
    try {
        const updatedSharing = await sharingApi().decline(props.sharing.id, comment.value);
        removeFromStore();
        emit('sharingUpdated', updatedSharing);
    } catch {
        toast.showError({
            content: "Une erreur est survenue lors de l'acceptation du partage. Veuillez réessayer plus tard.",
        });
    } finally {
        loadings.refuse = false;
    }
};
</script>

<template>
  <Dialog>
    <DialogTrigger as-child>
      <slot />
    </DialogTrigger>
    <DialogScrollContent class="max-w-3xl">
      <DialogTitle>
        Détails du partage
      </DialogTitle>

      <div class="space-y-6">
        <div class="border-b pb-4">
          <h3 class="text-lg font-semibold mb-2">
            {{ sharing.sharable_type === 'customer' ? 'Patient' : 'Bénéficiaire' }}
          </h3>
          <p class="text-gray-700">
            {{ sharing.sharable?.information?.name || 'Nom non disponible' }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="md:col-span-2">
            <h4 class="font-medium text-gray-900 mb-2">
              Informations de la demande
            </h4>
            <div class="space-y-2 text-sm">
              <p>
                <span class="font-medium">Demandeur:</span> {{ sharing.applicant?.name }} {{ sharing.tenant_name }}
              </p><p>
                <span class="font-medium">Date de demande:</span>
                {{ dateHelper.shortFormat(sharing.created_at) }}
              </p>
            </div>
          </div>

          <div>
            <h4 class="font-medium text-gray-900 mb-2">
              Statut
            </h4>
            <div class="space-y-2">
              <SharingStatus :status="sharing.status" />
              <div v-if="sharing.authorized_at" class="text-sm text-gray-600">
                <span class="font-medium">Date d'autorisation:</span>
                {{ dateHelper.shortFormat(sharing.authorized_at) }}
              </div>
              <div v-if="sharing.declined_at" class="text-sm text-gray-600">
                <span class="font-medium">Date de refus:</span>
                {{ dateHelper.shortFormat(sharing.declined_at) }}
              </div>
            </div>
          </div>
        </div>

        <div v-if="sharing.hospital_patient_files?.length" class="border-t pt-4">
          <h4 class="font-medium text-gray-900 mb-3">
            Fichiers ({{ sharing.hospital_patient_files.length }})
          </h4>
          <div class="space-y-2 bg-gray-50 p-3 rounded-lg">
            <HospitalPatientFileRow
              v-for="file in sharing.hospital_patient_files"
              :key="file.id"
              :file="file"
            />
          </div>
        </div>

        <div class="border-t pt-4">
          <div v-if="sharing.status === ISharingStatus.PENDING" class="space-y-4">
            <div>
              <label for="comment" class="block text-sm font-medium text-gray-700 mb-2">
                Commentaire (optionnel)
              </label>
              <Textarea
                id="comment"
                v-model="comment"
                rows="3"
                placeholder="Ajoutez un commentaire..."
              />
            </div>
            <div class="flex justify-end gap-3">
              <BaseButton
                density="comfortable"
                :disabled="loadings.accept || loadings.refuse"
                :loading="loadings.refuse"
                @click="declineSharing"
              >
                <span>Refuser</span>
              </BaseButton>
              <BaseButton
                primary
                density="comfortable"
                :loading="loadings.accept"
                :disabled="loadings.accept || loadings.refuse"
                @click="acceptSharing"
              >
                Accepter
              </BaseButton>
            </div>
          </div>

          <div v-else-if="sharing.status === ISharingStatus.AUTHORIZED" class="space-y-4">
            <div>
              <label for="refuse-comment" class="block text-sm font-medium text-gray-700 mb-2">
                Motif du refus
              </label>
              <Textarea
                id="refuse-comment"
                v-model="comment"
                rows="3"
                placeholder="Expliquez pourquoi vous refusez ce partage..."
              />
            </div>
            <div class="flex justify-end">
              <BaseButton
                :loading="loadings.refuse"
                density="comfortable"
                :disabled="loadings.accept || loadings.refuse || !comment.trim()"
                @click="declineSharing"
              >
                <span>Refuser le partage</span>
              </BaseButton>
            </div>
          </div>

          <div v-else-if="sharing.status === ISharingStatus.DECLINED" class="text-center text-gray-500">
            <p>Ce partage a été refusé.</p>
          </div>
        </div>
      </div>
    </DialogScrollContent>
  </Dialog>
</template>

<style scoped>

</style>
